import { ColorFinderImage } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateColorFinderImageArgs,
    getDefaultColorFinderImageCreateArgs,
    UpdateColorFinderImageArgs,
} from "@nova/serializer/src/client/validators/images/colorFinder"
import {
    addEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { pick } from "nodash"
import { maybeSetProjectLocation } from "../projectLocation"

const {
    create: createColorFinderImage,
    update: updateColorFinderImage,
    delete: deleteColorFinderImage,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    ColorFinderImage, // entity type
    CreateColorFinderImageArgs, // create args to send to server
    [
        "id",
        "longitude",
        "latitude",
        "altitude",
        "height",
        "pitch",
        "yaw",
        "roll",
        "takenAt",
        "fov",
        "hasLowQuality",
        "make",
        "size",
        "focalLength",
        "uploadedAt",
    ], // create args required by action
    UpdateColorFinderImageArgs, // update args
    { flightId: string } // context
>("ColorFinderImage")

export const addColorFinderImagesToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            if ("name" in data) delete data["name"]
            const optimisticData = pick(
                {
                    ...data,
                    ...getDefaultColorFinderImageCreateArgs(),
                } as Record<keyof ColorFinderImage, unknown>,
                Object.keys(ColorFinderImage.create()) as (keyof ColorFinderImage)[]
            ) as ColorFinderImage

            createColorFinderImage(socket, { ...data, id: optimisticData.id }, context)
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["imageColorFinderFlights", { id: context.flightId }, "images"],
                })
            )
            const project = store.getState().project.data
            const hasLocation =
                Number.isFinite(optimisticData.latitude) &&
                Number.isFinite(optimisticData.longitude)
            maybeSetProjectLocation(
                store,
                project.orgId,
                project.id,
                hasLocation
                    ? { latitude: optimisticData.latitude, longitude: optimisticData.longitude }
                    : null
            )
        })
        .addCase(updateAction, (store, { payload: { id, updates, context } }, socket) => {
            updateColorFinderImage(socket, id, updates, context)
            store.dispatch(
                updateEntitySocket({
                    updates,
                    path: ["imageColorFinderFlights", { id: context.flightId }, "images", { id }],
                })
            )
        })
        .addCase(deleteAction, (store, { payload: { id, context } }, socket) => {
            deleteColorFinderImage(socket, id, context)
            store.dispatch(
                deleteEntitySocket({
                    path: ["imageColorFinderFlights", { id: context.flightId }, "images", { id }],
                })
            )
        })

export {
    createAction as createColorFinderImage,
    updateAction as updateColorFinderImage,
    deleteAction as deleteColorFinderImage,
}
